import { useEffect, useState } from 'react';
import * as batchesApi from '../api/batches';
import type { BatchVersionSummary, BatchData } from '../types';

interface Props {
  open: boolean;
  batchId: string | null;
  onClose: () => void;
  onRestored: (data: BatchData) => void;
}

export default function VersionHistoryDrawer({ open, batchId, onClose, onRestored }: Props) {
  const [versions, setVersions] = useState<BatchVersionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);

  useEffect(() => {
    if (!open || !batchId) return;
    let cancelled = false;
    setLoading(true);
    batchesApi
      .listVersions(batchId)
      .then(({ versions }) => {
        if (!cancelled) {
          setVersions(versions);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setVersions([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, batchId]);

  async function handleRestore(v: BatchVersionSummary) {
    if (!batchId) return;
    if (
      !confirm(
        'Restore this version? Your current state will be saved as a version first so you can undo.',
      )
    )
      return;
    setRestoringId(v.id);
    try {
      const res = await batchesApi.restoreVersion(batchId, v.id);
      onRestored(res.data);
      onClose();
    } finally {
      setRestoringId(null);
    }
  }

  return (
    <div
      className={'drawer' + (open ? ' open' : '')}
      role="dialog"
      aria-label="Version history"
      aria-hidden={!open}
    >
      <div className="drawer-header">
        <span className="card-title">Version history</span>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="drawer-body">
        {loading && <div style={{ padding: '12px 18px', fontSize: 12, color: 'var(--ink3)' }}>Loading…</div>}
        {!loading && versions.length === 0 && (
          <div style={{ padding: '12px 18px', fontSize: 12, color: 'var(--ink3)' }}>
            No saved versions yet.
          </div>
        )}
        {versions.map((v) => (
          <div key={v.id} className="version-row" onClick={() => handleRestore(v)}>
            <div className="version-row-main">
              {new Date(v.saved_at).toLocaleString()}
            </div>
            <div className="version-row-sub">
              {v.saved_by ? `by ${v.saved_by}` : 'unknown'} · click to restore
              {restoringId === v.id ? ' (restoring…)' : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
